import { Controller } from "@hotwired/stimulus"

const WEEKDAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"]

export default class extends Controller {
  static targets = [
    "modal",
    "calendarGrid",
    "monthLabel",
    "selectedDateLabel",
    "booleanForm",
    "booleanDateInput",
    "booleanValueInput",
    "dateInput",
    "numberForm",
    "numberInput",
    "durationForm",
    "durationHoursInput",
    "durationMinutesInput",
    "durationTotalInput",
    "choiceForm",
    "choiceButton",
    "fastForm",
    "fastLogIdInput",
    "fastStartInput",
    "fastEndInput",
    "fastSaveButton",
    "fastEmptyHint"
  ]

  static values = {
    kind: String,
    code: String,
    maxDate: String
  }

  connect() {
    this.maxDate = this.maxDateValue ? this.parseDate(this.maxDateValue) : new Date()
    this.minDate = new Date(this.maxDate)
    this.minDate.setDate(this.minDate.getDate() - 364)
    this.logs = this.parseLogs()
    this.currentMonth = new Date(this.maxDate.getFullYear(), this.maxDate.getMonth(), 1)
    this.selectedDate = this.formatDate(this.maxDate)
    this.pendingUpdate = null
    this.render()
    this.setSelectedDate(this.selectedDate)
  }

  parseLogs() {
    const encoded = this.element.dataset.backfillCalendarLogsEncoded
    let raw = ""
    if (encoded) {
      try {
        raw = atob(encoded)
      } catch {
        raw = ""
      }
    }
    if (!raw) return {}
    try {
      const parsed = JSON.parse(raw)
      if (!this.minDate || !this.maxDate) return parsed
      const normalized = {}
      Object.entries(parsed).forEach(([date, log]) => {
        const d = this.parseDate(date)
        if (d < this.minDate || d > this.maxDate) return
        normalized[date] = log
      })
      return normalized
    } catch {
      return {}
    }
  }

  open() {
    this.logs = this.parseLogs()
    this.logs = Object.fromEntries(
      Object.entries(this.logs).map(([date, log]) => {
        return [date, this.normalizeLogPayload(log)]
      })
    )
    this.modalTarget.classList.remove("hidden")
    this.modalTarget.setAttribute("aria-hidden", "false")
    document.body.classList.add("overflow-hidden")
    this.render()
    this.setSelectedDate(this.selectedDate)
  }

  close() {
    this.modalTarget.classList.add("hidden")
    this.modalTarget.setAttribute("aria-hidden", "true")
    document.body.classList.remove("overflow-hidden")
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  prevMonth() {
    const prev = new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth() - 1, 1)
    if (this.monthWithinRange(prev)) {
      this.currentMonth = prev
      this.render()
    }
  }

  nextMonth() {
    const next = new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth() + 1, 1)
    if (this.monthWithinRange(next)) {
      this.currentMonth = next
      this.render()
    }
  }

  onGridClick(event) {
    const button = event.target.closest("button[data-date]")
    if (!button || button.dataset.disabled === "true") return

    const date = button.dataset.date
    if (this.kindValue === "boolean") {
      this.setSelectedDate(date)
      this.toggleBoolean(date)
    } else {
      this.setSelectedDate(date)
    }
  }

  onChoiceClick(event) {
    const button = event.currentTarget
    const value = button.dataset.choiceValue
    if (!value) return
    this.pendingUpdate = { date: this.selectedDate, kind: "choice", value }
  }

  onNumberSubmit() {
    if (!this.hasNumberInputTarget) return
    const value = this.numberInputTarget.value.trim()
    this.pendingUpdate = { date: this.selectedDate, kind: "number", value }
  }

  onDurationSubmit(event) {
    const hours = this.parseWholeNumber(this.hasDurationHoursInputTarget ? this.durationHoursInputTarget.value : "0")
    const minutes = this.parseWholeNumber(this.hasDurationMinutesInputTarget ? this.durationMinutesInputTarget.value : "0")
    const normalizedMinutes = Math.max(0, Math.min(59, minutes))
    const total = (hours * 60) + normalizedMinutes

    if (this.hasDurationTotalInputTarget) {
      this.durationTotalInputTarget.value = String(total)
    }

    if (this.codeValue === "sleep" && total <= 0) {
      event.preventDefault()
      return
    }

    this.pendingUpdate = { date: this.selectedDate, kind: "duration", value: total }
  }

  onFastSubmit() {
    this.pendingUpdate = { date: this.selectedDate, kind: "fasting" }
  }

  onSubmitEnd(event) {
    if (!event.detail.success || !this.pendingUpdate) return
    const { date, kind, value } = this.pendingUpdate
    if (kind === "boolean") {
      this.logs[date] = { boolean_value: value === true || value === "true" }
    } else if (kind === "number") {
      this.logs[date] = { number_value: value }
    } else if (kind === "duration") {
      this.logs[date] = { duration_minutes: Number(value) }
    } else if (kind === "choice") {
      const currentLog = this.normalizeLogPayload(this.logs[date] || {})
      const nextValues = this.toggleChoiceValues(this.choiceValuesForLog(currentLog), value)
      this.logs[date] = { ...currentLog, choice_values: nextValues }
    } else if (kind === "fasting") {
      const logId = this.hasFastLogIdInputTarget ? this.fastLogIdInputTarget.value : null
      if (logId) {
        this.logs[date] = {
          ...this.logs[date],
          id: logId,
          fast_start_at: this.localInputToIso(this.fastStartInputTarget?.value),
          fast_end_at: this.localInputToIso(this.fastEndInputTarget?.value)
        }
      }
    }
    this.pendingUpdate = null
    this.render()
    if (this.kindValue !== "boolean") {
      this.setSelectedDate(date)
    }
  }

  toggleBoolean(date) {
    if (!this.hasBooleanFormTarget) return
    const isCompleted = this.isCompleted(date)
    const nextValue = !isCompleted
    this.pendingUpdate = { date, kind: "boolean", value: nextValue }
    this.booleanDateInputTarget.value = date
    this.booleanValueInputTarget.value = nextValue ? "true" : "false"
    this.booleanFormTarget.requestSubmit()
  }

  setSelectedDate(date) {
    this.selectedDate = date
    if (this.hasSelectedDateLabelTarget) {
      this.selectedDateLabelTarget.textContent = this.formatHumanDate(date)
    }
    if (this.hasDateInputTarget) {
      this.dateInputTargets.forEach((input) => {
        input.value = date
      })
    }
    if (this.hasNumberInputTarget) {
      const log = this.logs[date]
      const value = log?.number_value ?? ""
      this.numberInputTarget.value = value
    }
    if (this.hasDurationHoursInputTarget || this.hasDurationMinutesInputTarget || this.hasDurationTotalInputTarget) {
      const log = this.logs[date]
      const total = this.durationTotalForLog(log)
      const hours = Math.floor(total / 60)
      const minutes = total % 60
      if (this.hasDurationHoursInputTarget) this.durationHoursInputTarget.value = total > 0 ? String(hours) : ""
      if (this.hasDurationMinutesInputTarget) this.durationMinutesInputTarget.value = total > 0 ? String(minutes) : ""
      if (this.hasDurationTotalInputTarget) this.durationTotalInputTarget.value = total > 0 ? String(total) : ""
    }
    if (this.hasChoiceButtonTarget) {
      const log = this.logs[date]
      const selectedValues = this.choiceValuesForLog(log)
      this.choiceButtonTargets.forEach((btn) => {
        const option = (btn.dataset.choiceValue || "").toString()
        const active = selectedValues.some((value) => value.toLowerCase() === option.toLowerCase())
        btn.classList.toggle("ring-2", Boolean(active))
        btn.classList.toggle("ring-[var(--habit-color)]/30", Boolean(active))
        btn.classList.toggle("opacity-100", Boolean(active))
        btn.classList.toggle("opacity-70", !active)
      })
    }
    if (this.kindValue === "fasting") {
      const log = this.logs[date]
      if (this.hasFastLogIdInputTarget) {
        this.fastLogIdInputTarget.value = log?.id || ""
      }
      if (this.hasFastStartInputTarget) {
        this.fastStartInputTarget.value = log?.fast_start_at ? this.formatDateTimeLocal(log.fast_start_at) : ""
      }
      if (this.hasFastEndInputTarget) {
        this.fastEndInputTarget.value = log?.fast_end_at ? this.formatDateTimeLocal(log.fast_end_at) : ""
      }
      if (this.hasFastSaveButtonTarget) {
        this.fastSaveButtonTarget.disabled = !log?.id
        this.fastSaveButtonTarget.classList.toggle("opacity-40", !log?.id)
      }
      if (this.hasFastEmptyHintTarget) {
        this.fastEmptyHintTarget.textContent = log?.id ? "" : "No completed fast logged for this date."
      }
    }
    this.highlightSelectedDate()
  }

  highlightSelectedDate() {
    const buttons = this.calendarGridTarget.querySelectorAll("button[data-date]")
    buttons.forEach((btn) => {
      const isSelected = btn.dataset.date === this.selectedDate
      btn.classList.toggle("ring-2", isSelected)
      btn.classList.toggle("ring-[var(--habit-color)]/40", isSelected)
      btn.classList.toggle("ring-offset-2", isSelected)
      btn.classList.toggle("ring-offset-white", isSelected)
      btn.classList.toggle("dark:ring-offset-[#1c1c1e]", isSelected)
    })
  }

  render() {
    this.updateMonthLabel()
    this.calendarGridTarget.innerHTML = this.renderGrid()
    this.highlightSelectedDate()
  }

  updateMonthLabel() {
    if (!this.hasMonthLabelTarget) return
    const label = this.currentMonth.toLocaleDateString("en-US", { month: "short", year: "numeric" })
    this.monthLabelTarget.textContent = label
  }

  renderGrid() {
    const monthStart = new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth(), 1)
    const daysInMonth = new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth() + 1, 0).getDate()
    const leadingBlanks = (monthStart.getDay() + 6) % 7
    const totalCells = Math.ceil((leadingBlanks + daysInMonth) / 7) * 7

    const cells = []
    const stripeColor = this.multiChoiceStripeColor()
    for (let i = 0; i < totalCells; i++) {
      const dayNumber = i - leadingBlanks + 1
      if (dayNumber < 1 || dayNumber > daysInMonth) {
        cells.push(`<div class="h-9"></div>`)
        continue
      }

      const date = new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth(), dayNumber)
      const dateStr = this.formatDate(date)
      const disabled = !this.dateWithinRange(date)
      const completed = this.isCompleted(dateStr)
      const multiChoiceDay = completed && this.isMultiChoiceDay(dateStr)
      const classes = [
        "h-9 w-9 rounded-xl text-sm font-semibold flex items-center justify-center transition",
        disabled ? "text-slate-400/60 dark:text-slate-500/60" : "text-slate-700 dark:text-white",
        completed ? "bg-[var(--habit-color)] text-white" : "bg-slate-100 hover:bg-slate-200 dark:bg-white/5 dark:hover:bg-white/10"
      ]
      if (multiChoiceDay) classes.push("relative", "overflow-hidden")
      const disabledAttr = disabled ? "true" : "false"
      const styleAttr = multiChoiceDay ? ` style="--multi-choice-stripe-color: ${stripeColor};"` : ""
      const overlay = multiChoiceDay ? `<span class="pointer-events-none absolute inset-0 heatmap-multi-choice-stripes" aria-hidden="true"></span>` : ""
      const label = multiChoiceDay ? `<span class="relative z-[1]">${dayNumber}</span>` : String(dayNumber)
      cells.push(
        `<button type="button" class="${classes.join(" ")}" data-date="${dateStr}" data-disabled="${disabledAttr}"${styleAttr} ${disabled ? "disabled" : ""}>${overlay}${label}</button>`
      )
    }

    const weekdayRow = WEEKDAY_LABELS.map((label) => `<div class="text-xs text-slate-400">${label}</div>`).join("")
    const cellsRow = cells.join("")
    return `
      <div class="grid grid-cols-7 gap-2 text-center mb-2">${weekdayRow}</div>
      <div class="grid grid-cols-7 gap-2">${cellsRow}</div>
    `
  }

  isCompleted(dateStr) {
    const log = this.logs[dateStr]
    if (!log) return false
    if (this.kindValue === "boolean") return this.truthy(log.boolean_value)
    if (this.kindValue === "number") return log.number_value != null && log.number_value !== ""
    if (this.kindValue === "duration") return this.durationTotalForLog(log) >= 1
    if (this.kindValue === "choice") return this.choiceValuesForLog(log).length > 0
    if (this.kindValue === "fasting") return log.fast_end_at != null && log.fast_end_at !== ""
    return false
  }

  isMultiChoiceDay(dateStr) {
    if (this.kindValue !== "choice") return false
    const log = this.logs[dateStr]
    if (!log) return false
    return this.choiceValuesForLog(log).length > 1
  }

  multiChoiceStripeColor() {
    const habitColor = this.element?.style?.getPropertyValue("--habit-color")?.trim() || ""
    const normalized = this.normalizeHexColor(habitColor)
    if (!normalized) return "rgba(255,255,255,0.28)"

    const red = Number.parseInt(normalized.slice(0, 2), 16)
    const green = Number.parseInt(normalized.slice(2, 4), 16)
    const blue = Number.parseInt(normalized.slice(4, 6), 16)
    const yiq = ((red * 299) + (green * 587) + (blue * 114)) / 1000
    return yiq >= 170 ? "rgba(0,0,0,0.20)" : "rgba(255,255,255,0.28)"
  }

  normalizeHexColor(color) {
    const raw = (color || "").toString().trim().replace(/^#/, "")
    if (/^[0-9a-fA-F]{3}$/.test(raw)) {
      return raw.split("").map((char) => `${char}${char}`).join("").toLowerCase()
    }
    if (/^[0-9a-fA-F]{6}$/.test(raw)) {
      return raw.toLowerCase()
    }
    if (/^[0-9a-fA-F]{8}$/.test(raw)) {
      return raw.slice(0, 6).toLowerCase()
    }
    return null
  }

  durationTotalForLog(log) {
    if (!log) return 0
    const value = this.parseWholeNumber(log.duration_minutes)
    return value >= 1 ? value : 0
  }

  truthy(value) {
    return value === true || value === "true" || value === "t" || value === 1 || value === "1"
  }

  parseWholeNumber(value) {
    const parsed = parseInt((value || "").toString().trim(), 10)
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0
  }

  normalizeLogPayload(log) {
    const normalized = {}
    Object.entries(log || {}).forEach(([key, value]) => {
      if (key.includes("boolean")) {
        normalized[key] = value === true || value === "true" || value === "t" || value === 1 || value === "1"
        return
      }

      if (key === "choice_values") {
        normalized[key] = Array.isArray(value)
          ? value.map((item) => item.toString().trim()).filter(Boolean)
          : []
        return
      }

      normalized[key] = value
    })

    if (!normalized.choice_values && normalized.choice_value) {
      normalized.choice_values = [normalized.choice_value.toString().trim()].filter(Boolean)
    }

    if (!normalized.choice_values) normalized.choice_values = []
    delete normalized.choice_value
    return normalized
  }

  choiceValuesForLog(log) {
    return this.normalizeLogPayload(log || {}).choice_values
  }

  toggleChoiceValues(currentValues, toggledValue) {
    const value = (toggledValue || "").toString().trim()
    if (!value) return currentValues

    const deduped = []
    currentValues.forEach((item) => {
      const token = item.toString().trim()
      if (!token) return
      if (deduped.some((existing) => existing.toLowerCase() === token.toLowerCase())) return
      deduped.push(token)
    })

    const exists = deduped.some((item) => item.toLowerCase() === value.toLowerCase())
    let nextValues = exists
      ? deduped.filter((item) => item.toLowerCase() !== value.toLowerCase())
      : [...deduped, value]

    if (this.codeValue === "exercise") {
      const toggledIsRest = value.toLowerCase() === "rest"
      if (toggledIsRest && !exists) {
        nextValues = nextValues.filter((item) => item.toLowerCase() === "rest")
      } else if (!toggledIsRest) {
        nextValues = nextValues.filter((item) => item.toLowerCase() !== "rest")
      }
    }

    return nextValues
  }

  dateWithinRange(date) {
    return date >= this.minDate && date <= this.maxDate
  }

  monthWithinRange(monthStart) {
    const monthEnd = new Date(monthStart.getFullYear(), monthStart.getMonth() + 1, 0)
    return monthEnd >= this.minDate && monthStart <= this.maxDate
  }

  parseDate(dateStr) {
    const [year, month, day] = dateStr.split("-").map(Number)
    return new Date(year, month - 1, day)
  }

  formatDate(date) {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, "0")
    const day = String(date.getDate()).padStart(2, "0")
    return `${year}-${month}-${day}`
  }

  formatHumanDate(dateStr) {
    const date = this.parseDate(dateStr)
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
  }

  formatDateTimeLocal(value) {
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return ""
    const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000)
    return local.toISOString().slice(0, 16)
  }

  localInputToIso(value) {
    if (!value) return null
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return null
    return date.toISOString()
  }
}
